import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { CrmStatusApiAdapter } from '../adapters/CrmStatusApiAdapter';
import { ModifyOrderStatusRequest, CreateStatusRequest, UpdateStatusRequest } from '../models/CRM';

export const useCrmStatus = (enabled = true) => {
  const httpClient = createHttpClient(apiClientWH);
  const apiAdapter = new CrmStatusApiAdapter(httpClient);

  const query = useQuery({
    queryKey: ['crm-status'],
    queryFn: () => apiAdapter.getStatusList(),
    enabled,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  return {
    statusList: query.data ?? [],
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    refetch: query.refetch,
  };
};

export const useModifyOrderStatus = () => {
  const httpClient = createHttpClient(apiClientWH);
  const apiAdapter = new CrmStatusApiAdapter(httpClient);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ModifyOrderStatusRequest) =>
      apiAdapter.modifyOrderStatus(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['crm-status'] });
      // No invalidamos 'crm-orders' para evitar refetch automático (actualizamos localmente)
    },
  });
};

export const useCreateStatus = () => {
  const httpClient = createHttpClient(apiClientWH);
  const apiAdapter = new CrmStatusApiAdapter(httpClient);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateStatusRequest) => apiAdapter.createStatus(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['crm-status'] });
      queryClient.invalidateQueries({ queryKey: ['crm-orders'] });
    },
  });
};

export const useUpdateStatus = () => {
  const httpClient = createHttpClient(apiClientWH);
  const apiAdapter = new CrmStatusApiAdapter(httpClient);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ statusId, data }: { statusId: number; data: UpdateStatusRequest }) =>
      apiAdapter.updateStatus(statusId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['crm-status'] });
      queryClient.invalidateQueries({ queryKey: ['crm-orders'] });
    },
  });
};

export const useDeleteStatus = () => {
  const httpClient = createHttpClient(apiClientWH);
  const apiAdapter = new CrmStatusApiAdapter(httpClient);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (statusId: number) => apiAdapter.deleteStatus(statusId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['crm-status'] });
      queryClient.invalidateQueries({ queryKey: ['crm-orders'] });
    },
  });
};
